import { BoundingBox } from '@newroom-connect/library/types';
import { ICornerCoordinates, IDimensions, ISphericalCoords, IScreenCoords } from '@newroom-connect/library/interfaces';

/**
 * Helper class for transform coordinate operations.
 * Handles corner position conversions and scaling operations.
 */
export class TransformCoordinateHelper {
  /**
   * Convert a single point to percentage (0-100 range).
   * @param point - Point coordinates in pixels.
   * @param dimensions - Container dimensions.
   * @returns Point coordinates as percentage.
   */
  public static pointToPercentage(point: IScreenCoords, dimensions: IDimensions): IScreenCoords {
    return {
      x: (point.x / dimensions.width) * 100,
      y: (point.y / dimensions.height) * 100
    };
  }

  /**
   * Convert a single point from percentage to pixels.
   * @param point - Point coordinates as percentage (0-100).
   * @param dimensions - Container dimensions.
   * @returns Point coordinates in pixels.
   */
  public static percentageToPixels(point: IScreenCoords, dimensions: IDimensions): IScreenCoords {
    return {
      x: (point.x / 100) * dimensions.width,
      y: (point.y / 100) * dimensions.height
    };
  }

  /**
   * Scale a single point from one dimension to another.
   * @param point - Point coordinates to scale.
   * @param fromSize - Original dimensions.
   * @param toSize - Target dimensions.
   * @returns Scaled point coordinates.
   */
  public static scalePoint(point: IScreenCoords, fromSize: IDimensions, toSize: IDimensions): IScreenCoords {
    const scaleX = toSize.width / fromSize.width;
    const scaleY = toSize.height / fromSize.height;

    return {
      x: point.x * scaleX,
      y: point.y * scaleY
    };
  }

  /**
   * Normalize a single point to 0-1 range.
   * @param point - Point coordinates in pixels.
   * @param dimensions - Container dimensions.
   * @returns Normalized point coordinates (0-1 range).
   */
  public static normalizePoint(point: IScreenCoords, dimensions: IDimensions): IScreenCoords {
    return {
      x: dimensions.width > 0 ? point.x / dimensions.width : 0,
      y: dimensions.height > 0 ? point.y / dimensions.height : 0
    };
  }

  /**
   * Denormalize a single point from 0-1 range to pixels.
   * @param point - Normalized point coordinates (0-1 range).
   * @param dimensions - Container dimensions.
   * @returns Point coordinates in pixels.
   */
  public static denormalizePoint(point: IScreenCoords, dimensions: IDimensions): IScreenCoords {
    return {
      x: point.x * dimensions.width,
      y: point.y * dimensions.height
    };
  }

  /**
   * Convert corner positions to percentages (0-100 range).
   * @param corners - Corner coordinates in pixels.
   * @param dimensions - Container dimensions.
   * @returns Corner coordinates as percentages.
   */
  public static cornersToPercentages(corners: ICornerCoordinates, dimensions: IDimensions): ICornerCoordinates {
    return {
      topLeft: this.pointToPercentage(corners.topLeft, dimensions),
      topRight: this.pointToPercentage(corners.topRight, dimensions),
      bottomLeft: this.pointToPercentage(corners.bottomLeft, dimensions),
      bottomRight: this.pointToPercentage(corners.bottomRight, dimensions)
    };
  }

  /**
   * Convert corner positions from percentages to pixels.
   * @param percentageCorners - Corner coordinates as percentages (0-100).
   * @param dimensions - Container dimensions.
   * @returns Corner coordinates in pixels.
   */
  public static percentagesToPixels(percentageCorners: ICornerCoordinates, dimensions: IDimensions): ICornerCoordinates {
    return {
      topLeft: this.percentageToPixels(percentageCorners.topLeft, dimensions),
      topRight: this.percentageToPixels(percentageCorners.topRight, dimensions),
      bottomLeft: this.percentageToPixels(percentageCorners.bottomLeft, dimensions),
      bottomRight: this.percentageToPixels(percentageCorners.bottomRight, dimensions)
    };
  }

  /**
   * Scale corner positions from one dimension to another.
   * @param corners - Corner coordinates to scale.
   * @param fromSize - Original dimensions.
   * @param toSize - Target dimensions.
   * @returns Scaled corner coordinates.
   */
  public static scaleCorners(corners: ICornerCoordinates, fromSize: IDimensions, toSize: IDimensions): ICornerCoordinates {
    return {
      topLeft: this.scalePoint(corners.topLeft, fromSize, toSize),
      topRight: this.scalePoint(corners.topRight, fromSize, toSize),
      bottomLeft: this.scalePoint(corners.bottomLeft, fromSize, toSize),
      bottomRight: this.scalePoint(corners.bottomRight, fromSize, toSize)
    };
  }

  /**
   * Normalize corner positions to 0-1 range.
   * @param corners - Corner coordinates in pixels.
   * @param dimensions - Container dimensions.
   * @returns Normalized corner coordinates (0-1 range).
   */
  public static normalizeCorners(corners: ICornerCoordinates, dimensions: IDimensions): ICornerCoordinates {
    return {
      topLeft: this.normalizePoint(corners.topLeft, dimensions),
      topRight: this.normalizePoint(corners.topRight, dimensions),
      bottomLeft: this.normalizePoint(corners.bottomLeft, dimensions),
      bottomRight: this.normalizePoint(corners.bottomRight, dimensions)
    };
  }

  /**
   * Denormalize corner positions from 0-1 range to pixels.
   * @param normalizedCorners - Normalized corner coordinates (0-1 range).
   * @param dimensions - Container dimensions.
   *
   * @returns Corner coordinates in pixels.
   */
  public static denormalizeCorners(normalizedCorners: ICornerCoordinates, dimensions: IDimensions): ICornerCoordinates {
    return {
      topLeft: this.denormalizePoint(normalizedCorners.topLeft, dimensions),
      topRight: this.denormalizePoint(normalizedCorners.topRight, dimensions),
      bottomLeft: this.denormalizePoint(normalizedCorners.bottomLeft, dimensions),
      bottomRight: this.denormalizePoint(normalizedCorners.bottomRight, dimensions)
    };
  }

  /**
   * Adjust saved corner positions to current overlay size.
   * Handles normalization, percentage, and reference size conversions.
   * @param savedCorners - Saved corner coordinates.
   * @param currentDimensions - Current container dimensions.
   * @param isNormalized - Whether corners are saved as percentages.
   * @param referenceSize - Reference dimensions if corners were saved at different size.
   *
   * @returns Adjusted corner coordinates for current size.
   */
  public static adjustCornersToCurrentSize(savedCorners: ICornerCoordinates, currentDimensions: IDimensions, isNormalized?: boolean, referenceSize?: IDimensions): ICornerCoordinates {
    // If saved as percentages (0-100), convert to pixels
    if (isNormalized) {
      return this.percentagesToPixels(savedCorners, currentDimensions);
    }

    // If we have a reference size and it differs from current, scale accordingly
    if (referenceSize
        && referenceSize.width > 0
        && referenceSize.height > 0
        && (referenceSize.width !== currentDimensions.width || referenceSize.height !== currentDimensions.height)
    ) {
      return this.scaleCorners(savedCorners, referenceSize, currentDimensions);
    }

    // Already in correct pixel format for current size
    return savedCorners;
  }

  /**
   * Get spherical position from a bounding box.
   * Uses explicit spherical coordinates if available, otherwise converts from percentage-based positioning.
   *
   * @param boundingBox - The bounding box to get position from.
   * @returns Spherical coordinates (yaw, pitch in radians).
   */
  public static getSphericalPositionFromBoundingBox(boundingBox: BoundingBox): ISphericalCoords {
    // Use explicit spherical coordinates if available
    if (boundingBox.spherical) {
      return {
        yaw: boundingBox.spherical.yaw,
        pitch: boundingBox.spherical.pitch
      };
    }

    // Convert percentage-based positioning to spherical coordinates
    const yaw = (boundingBox.left / 100) * 2 * Math.PI - Math.PI;
    const pitch = (0.5 - boundingBox.top / 100) * Math.PI;

    return { yaw, pitch };
  }
}
